import React from "react";
import documentIcon from "../assets/icons/document.png";
import downloadIcon from "../assets/icons/download_green.svg";
import redirectIcon from "../assets/icons/redirect.png";
import { useTranslation } from 'react-i18next';

const DownloadCard = ({
  title,
  titleEn,
  description,
  descriptionEn,
  downloadLink,
  isDisabled = false,
  tooltipText = "कर अहवाल उपलब्ध नाही",
  tooltipTextEn = "Tax report is not available",
  buttonLabel = "Download",
  buttonLabelEn = "Download",
  backgroundColor = "white",
  icon = documentIcon,
  titleStyle,
  descriptionStyle,
  iconClassName,
  // Mobile App props
  showMobileApp = false,
  mobileAppLink,
  mobileAppLabel = "Mobile App",
  mobileAppLabelEn = "Mobile App",
  isExternalLink = false
}) => {
  const { i18n } = useTranslation();

  // Convert backgroundColor to background style (handles both solid colors and gradients)
  const backgroundStyle = backgroundColor.includes('gradient')
    ? { background: backgroundColor }
    : { backgroundColor };

  // Use English text if language is 'en', otherwise use Marathi
  const displayTitle = i18n.language === 'en' ? titleEn : title;
  const displayDescription = i18n.language === 'en' ? descriptionEn : description;
  const displayButtonLabel = i18n.language === 'en' ? buttonLabelEn : buttonLabel;
  const displayMobileAppLabel = i18n.language === 'en' ? mobileAppLabelEn : mobileAppLabel;

  const isButtonDisabled = isDisabled || !downloadLink;
  const unavailableTooltip = i18n.language === 'en'
    ? (tooltipTextEn || "Tax report is not available")
    : (tooltipText || "कर अहवाल उपलब्ध नाही");

  const handleClick = async () => {
    if (isButtonDisabled || !downloadLink) return;

    let targetLink = downloadLink;
    if (window.location.protocol === 'https:' && targetLink.startsWith('http:')) {
      targetLink = targetLink.replace(/^http:/, 'https:');
    }

    if (isExternalLink) {
      window.open(targetLink, '_blank', 'noopener,noreferrer');
    } else {
      try {
        // Fetch the file and download it as a blob
        const response = await fetch(targetLink);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("text/html")) {
          throw new Error("Received HTML instead of valid file");
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const anchor = document.createElement("a");
        anchor.href = blobUrl;
        let filename = "document.pdf";
        try {
          const rawName = targetLink.split("/").pop();
          if (rawName) filename = decodeURIComponent(rawName);
        } catch (e) {
          filename = targetLink.split("/").pop() || "document.pdf";
        }
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        // Clean up
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      } catch (error) {
        console.error("Download failed:", error);
        // Fallback: try direct download or open in new tab
        window.open(targetLink, '_blank');
      }
    }
  };

  const handleMobileAppClick = () => {
    if (!mobileAppLink) return;
    window.open(mobileAppLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={backgroundStyle} className="border rounded-md border-[#DDDDDD] p-6 h-full flex flex-col">
      <div>
        <img loading="lazy" src={icon} alt="card icon" className={`w-7 h-7 *: ${iconClassName || ''}`} />
      </div>
      <h2 style={titleStyle} className="text-2xl font-semibold m-0 mt-3">{displayTitle}</h2>
      {description && (
        <p style={descriptionStyle} className="text-[#727272] m-0 text-[13px] font-normal mt-2  leading-[1.5] tracking-wide">
          {displayDescription}
        </p>
      )}
      <div className="flex-grow"></div>
      <div className={`flex gap-3 mt-4 ${showMobileApp ? 'flex-row' : ''}`}>
        <button
          onClick={handleClick}
          disabled={isButtonDisabled}
          title={isButtonDisabled ? unavailableTooltip : undefined}
          className={`${
            isButtonDisabled
              ? "bg-gray-400 cursor-not-allowed opacity-75 text-white"
              : "bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white"
          } p-2 px-3 flex items-center justify-center gap-2 text-[14px] font-medium rounded-md transition w-fit`}
        >
          <img loading="lazy"
            className="w-4 h-4 filter brightness-0 invert opacity-90"
            src={isExternalLink ? redirectIcon : downloadIcon}
            alt={isExternalLink ? "Redirect Icon" : "Download Icon"}
          />
          {displayButtonLabel}
        </button>

        {showMobileApp && mobileAppLink && (
          <button
            onClick={handleMobileAppClick}
            className="border border-[var(--color-primary)] p-2 px-3 flex items-center justify-center gap-2 text-[14px] font-medium text-[var(--color-primary)] rounded-md hover:bg-[var(--color-primary)] hover:text-white transition w-fit"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17,19H7V5H17M17,1H7C5.89,1 5,1.89 5,3V21A2,2 0 0,0 7,23H17A2,2 0 0,0 19,21V3C19,1.89 18.1,1 17,1Z" />
            </svg>
            {displayMobileAppLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default DownloadCard;
